import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, Loader2, MessagesSquare, GitCompareArrows, FileText, Layers,
    ClipboardCheck, Timer, Pencil, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DocumentCard } from "@/components/library/DocumentCard";
import { UploadDialog } from "@/components/library/UploadDialog";
import { AddSourceDialog } from "@/components/workspace/AddSourceDialog";
import { CreateWorkspaceDialog } from "@/components/workspace/CreateWorkspaceDialog";
import { WorkspaceChat } from "@/components/workspace/WorkspaceChat";
import { WorkspaceInsights } from "@/components/workspace/WorkspaceInsights";
import { WorkspaceSummary } from "@/components/workspace/WorkspaceSummary";
import { FlashcardDeck } from "@/components/learning/FlashcardDeck";
import { QuizConfig } from "@/components/learning/QuizConfig";
import { QuizRunner } from "@/components/learning/QuizRunner";
import { ScoreRings } from "@/components/learning/ScoreRings";
import { workspacesApi, docsApi } from "@/lib/apiClient";
import { iconFor, accentFor } from "@/components/workspace/workspaceMeta";
import { useTranslation } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Workspace() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [ws, setWs] = useState(null);
    const [scores, setScores] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("chat");
    const [editOpen, setEditOpen] = useState(false);
    // quiz tab
    const [quizMode, setQuizMode] = useState("quiz");
    const [quizConfig, setQuizConfig] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await workspacesApi.get(id);
            setWs(data);
            if ((data.sources || []).length === 0) setTab("sources");
        } catch (e) {
            toast.error(t("workspace.loadFailed"));
            navigate("/workspaces");
        } finally { setLoading(false); }
    }, [id, navigate, t]);

    const loadScores = useCallback(async () => {
        try { setScores(await workspacesApi.scores(id)); } catch (e) { /* ignore */ }
    }, [id]);

    useEffect(() => { load(); loadScores(); }, [load, loadScores]);

    // Poll while any source is still processing.
    useEffect(() => {
        const processing = (ws?.sources || []).some((s) => s.status === "processing");
        if (!processing) return;
        const iv = setInterval(load, 3000);
        return () => clearInterval(iv);
    }, [ws, load]);

    const onDeleteSource = async (docId) => {
        setWs((w) => ({ ...w, sources: w.sources.filter((s) => s.id !== docId), source_count: (w.source_count || 1) - 1 }));
        try { await docsApi.remove(docId); toast.success(t("library.deleted")); }
        catch (e) { toast.error(t("library.deleteFailed")); load(); }
    };
    const onRetrySource = async (docId) => {
        try { const u = await docsApi.retry(docId); setWs((w) => ({ ...w, sources: w.sources.map((s) => (s.id === docId ? { ...s, ...u } : s)) })); toast.info(t("card.retrying")); }
        catch (e) { toast.error(t("library.processingFailed")); }
    };

    if (loading || !ws) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;

    const Icon = iconFor(ws.icon);
    const accent = accentFor(ws.color);
    const name = ws.is_default ? t("workspace.defaultName") : ws.name;
    const sources = ws.sources || [];
    const readyCount = sources.filter((s) => s.status === "ready").length;

    const TABS = [
        { key: "chat", label: t("workspace.tabs.chat"), icon: MessagesSquare },
        { key: "compare", label: t("workspace.tabs.compare"), icon: GitCompareArrows },
        { key: "summary", label: t("workspace.tabs.summary"), icon: FileText },
        { key: "cards", label: t("workspace.tabs.cards"), icon: Layers },
        { key: "quiz", label: t("workspace.tabs.quiz"), icon: ClipboardCheck },
        { key: "sources", label: t("workspace.tabs.sources"), icon: BookOpen },
    ];

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-8">
            <button data-testid="workspace-back" onClick={() => navigate("/workspaces")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-t3 transition-colors hover:text-t1">
                <ArrowLeft className="h-4 w-4" /> {t("workspace.backToHub")}
            </button>

            {/* Header */}
            <div className="flex flex-col gap-4 rounded-2xl border border-border bg-surface-1 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${accent}22`, color: accent }}><Icon className="h-7 w-7" /></div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="font-sans-display text-xl font-semibold tracking-tight text-t1 sm:text-2xl">{name}</h1>
                            <button data-testid="workspace-edit-header" onClick={() => setEditOpen(true)} className="rounded-lg p-1.5 text-t4 transition-colors hover:bg-surface-3 hover:text-t1"><Pencil className="h-3.5 w-3.5" /></button>
                        </div>
                        {ws.description && <p className="mt-1 max-w-xl text-sm text-t3">{ws.description}</p>}
                        <div className="mt-2 flex items-center gap-4 text-t4">
                            <span className="inline-flex items-center gap-1.5 text-xs"><FileText className="h-3.5 w-3.5" /> {t("workspace.card.sources", { n: sources.length })}</span>
                            <span className="inline-flex items-center gap-1.5 text-xs"><Layers className="h-3.5 w-3.5" /> {t("workspace.card.cards", { n: ws.flashcard_count || 0 })}</span>
                        </div>
                    </div>
                </div>
                {scores && <div className="flex w-full justify-center rounded-2xl border border-border bg-surface-2/50 p-3 sm:w-auto sm:shrink-0"><ScoreRings scores={scores.scores} size={64} /></div>}
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "quiz") setQuizConfig(null); }} className="mt-6">
                <TabsList className="flex w-full flex-wrap justify-center gap-1 bg-surface-2 p-1">
                    {TABS.map(({ key, label, icon: Ic }) => (
                        <TabsTrigger key={key} value={key} data-testid={`workspace-tab-${key}`} className="gap-1.5 text-xs data-[state=active]:bg-surface-1"><Ic className="h-3.5 w-3.5" /> {label}</TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="chat" className="mt-5">
                    <WorkspaceChat wsId={id} sourceCount={readyCount} />
                </TabsContent>

                <TabsContent value="compare" className="mt-5">
                    <WorkspaceInsights wsId={id} sourceCount={readyCount} />
                </TabsContent>

                <TabsContent value="summary" className="mt-5">
                    <WorkspaceSummary wsId={id} sourceCount={readyCount} />
                </TabsContent>

                <TabsContent value="cards" className="mt-5">
                    <FlashcardDeck
                        docId={id}
                        onChanged={loadScores}
                        api={{
                            list: () => workspacesApi.flashcardsList(id),
                            generate: (opts) => workspacesApi.flashcardsGenerate(id, opts),
                        }}
                    />
                </TabsContent>

                <TabsContent value="quiz" className="mt-5">
                    {!quizConfig ? (
                        <div>
                            <div className="mb-4 inline-flex rounded-xl border border-border bg-surface-2 p-1">
                                {[{ m: "quiz", icon: ClipboardCheck, label: t("workspace.quizMode") }, { m: "exam", icon: Timer, label: t("workspace.examMode") }].map(({ m, icon: Ic, label }) => (
                                    <button key={m} data-testid={`ws-quizmode-${m}`} onClick={() => setQuizMode(m)}
                                        className={cn("flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors", quizMode === m ? "bg-surface-1 text-t1" : "text-t3 hover:text-t1")}>
                                        <Ic className="h-3.5 w-3.5" /> {label}
                                    </button>
                                ))}
                            </div>
                            {readyCount === 0 ? (
                                <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-14 text-center text-sm text-t3">{t("workspace.chat.needSource")}</div>
                            ) : (
                                <QuizConfig mode={quizMode} onStart={(cfg) => setQuizConfig(cfg)} />
                            )}
                        </div>
                    ) : (
                        <QuizRunner
                            config={quizConfig}
                            generateFn={(cfg) => workspacesApi.quizGenerate(id, cfg)}
                            onExit={() => { setQuizConfig(null); loadScores(); }}
                            onScored={loadScores}
                        />
                    )}
                </TabsContent>

                <TabsContent value="sources" className="mt-5">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="font-sans-display text-sm font-semibold text-t2">{t("workspace.sourcesTitle")}</h2>
                        <div className="flex items-center gap-2">
                            <AddSourceDialog wsId={id} onAdded={() => { load(); loadScores(); }} />
                            <UploadDialog workspaceId={id} onUploaded={() => load()} />
                        </div>
                    </div>
                    {sources.length === 0 ? (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><BookOpen className="h-7 w-7" /></div>
                            <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("workspace.sourcesEmptyTitle")}</h3>
                            <p className="mt-1.5 max-w-md text-sm text-t3">{t("workspace.sourcesEmptyBody")}</p>
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                                <AddSourceDialog wsId={id} onAdded={() => { load(); loadScores(); }} />
                                <UploadDialog workspaceId={id} onUploaded={() => load()} />
                            </div>
                        </motion.div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {sources.map((doc, i) => <DocumentCard key={doc.id} doc={doc} index={i} onDelete={onDeleteSource} onRetry={onRetrySource} />)}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <CreateWorkspaceDialog existing={ws} open={editOpen} onOpenChange={setEditOpen} onSaved={(u) => setWs((w) => ({ ...w, ...u }))} />
        </div>
    );
}
